import { parseLocationCode, type PackScanResult } from "@shoe-trace/shared";
import {
  activateUnit,
  arrivalProgress,
  getOrder,
  listArrivals,
  listOrders,
  putaway,
  resolvePutaway,
  shipOrder,
  verifyScan,
  type ArrivalProgress,
  type ArrivalSummary,
  type OutboundOrder,
  type PutawayTarget,
} from "../api/client";

export interface ScanRejected {
  ok: false;
  message: string;
}

export interface ArrivalScanOk {
  ok: true;
  serial: string;
  status: string;
  humanCode: string;
  batchNo: string;
  message: string;
}

export interface PutawaySubmitOk {
  ok: true;
  locationCode: string;
  bound: number;
  left: number;
  message: string;
}

export interface VerifySubmitOk {
  ok: true;
  result: PackScanResult;
  order: OutboundOrder | null;
}

export function passFinished(result: PackScanResult): boolean {
  return result.status === "PASS" && result.verifiedQty >= result.requiredQty;
}

export interface VerifyOutcome {
  tone: "ok" | "bad";
  title: string;
  serial: string | null;
  summary: string | null;
  emphasis: string[];
  lines: string[];
  hold: boolean;
}

export interface SkuTally {
  humanCode: string;
  scanned: number;
  planned: number;
  left: number;
}

export const SHIP_DONE = "这张出库单已出库。";

const ACTIVATE_FALLBACK =
  "这张标没有扫出来。请换备用扫码枪再扫一次。还是不行，到异常中心走脱落补挂，不要自己编一个号码。";

const ORDER_STATUS_LABEL: Record<string, string> = {
  OPEN: "待拣货",
  IN_PROGRESS: "复核中",
  SHIPPED: "已出库",
};

export function errorText(error: unknown, fallback: string): string {
  if (error instanceof Error && error.message.trim()) return error.message;
  return fallback;
}

export function orderStatusLabel(status: string): string {
  return ORDER_STATUS_LABEL[status] ?? status;
}

export function formatTally(tally: { scanned: number; planned: number; left: number }): string {
  return `已扫 ${tally.scanned} / 计划 ${tally.planned} / 还剩 ${tally.left}`;
}

export function arrivalTally(plannedQty: number, activated: number): { scanned: number; planned: number; left: number } {
  return {
    scanned: activated,
    planned: plannedQty,
    left: Math.max(plannedQty - activated, 0),
  };
}

export function skuTally(line: { humanCode: string; plannedQty: number; activated: number }): SkuTally {
  return {
    humanCode: line.humanCode,
    scanned: line.activated,
    planned: line.plannedQty,
    left: Math.max(line.plannedQty - line.activated, 0),
  };
}

export function formatVerified(verified: number, required: number): string {
  return `已复核 ${verified} / 计划 ${required}`;
}

export function formatLineVerified(humanCode: string, verified: number, required: number): string {
  return `${humanCode} 已复核 ${verified} / 计划 ${required}`;
}

export function parseLocationScan(
  code: string,
  rejectMessage = "这不是库位码。请扫库位上的那张，不要扫鞋盒标。"
): { ok: true; locationCode: string } | ScanRejected {
  const parsed = parseLocationCode(code);
  if (!parsed) return { ok: false, message: rejectMessage };
  return { ok: true, locationCode: parsed.code };
}

export function locationScanBlock(
  location: {
    status: "NORMAL" | "FULL" | "LOCKED";
    occupied: number;
    capacity: number;
    groupKey: string | null;
    arrivalNo: string | null;
  } | undefined,
  incomingGroupKey: string
): string | null {
  if (!location) return null;
  if (location.status === "LOCKED") return "这个库位锁着，不能上架。请换一个库位，或让主管先解开。";
  if (location.occupied > 0 && location.groupKey !== incomingGroupKey) {
    if (location.arrivalNo) {
      return `这个库位放的是到货单 ${location.arrivalNo}。请换一个空位，不要和另一张到货单混放。`;
    }
    return "这个库位已经放着别的货。请换一个空位，不要混放。";
  }
  if (location.occupied >= location.capacity) return "这个库位已经放满了。请换一个还有空位的库位。";
  return null;
}

export function describeVerifyOutcome(result: PackScanResult): VerifyOutcome {
  if (result.status === "PASS") {
    return {
      tone: "ok",
      title: result.title,
      serial: result.serial,
      summary: `${result.serial} · ${result.batchNo} · ${result.humanCode}`,
      emphasis: [],
      lines: [result.message, result.peelHint],
      hold: false,
    };
  }
  if (result.status === "BLOCKED") {
    const where = result.earlierLocationCode
      ? `去这个库位取：${result.earlierLocationCode}`
      : "去哪个库位：系统里还没有这批的库位。不要猜库位，让主管打开库位页核对。";
    return {
      tone: "bad",
      title: result.title,
      serial: null,
      summary: null,
      emphasis: [`更早的批次：${result.earlierBatchNo}`, where],
      lines: [
        `你扫到的是批次 ${result.scannedBatchNo}，比应发的这批晚 ${result.daysDiff} 天。把这双放回原处。`,
        result.message,
      ],
      hold: true,
    };
  }
  return {
    tone: "bad",
    title: result.title,
    serial: null,
    summary: null,
    emphasis: [],
    lines: [result.message],
    hold: false,
  };
}

async function submitActivate(
  scannedCode: string,
  scope: { printTaskId?: string; arrivalNo?: string }
): Promise<ArrivalScanOk | ScanRejected> {
  try {
    const result = await activateUnit(scannedCode, scope);
    return {
      ok: true,
      serial: result.serial,
      status: result.status,
      humanCode: result.humanCode,
      batchNo: result.batchNo,
      message: result.message,
    };
  } catch (error: unknown) {
    return { ok: false, message: errorText(error, ACTIVATE_FALLBACK) };
  }
}

export function submitArrivalScan(arrivalNo: string, scannedCode: string): Promise<ArrivalScanOk | ScanRejected> {
  return submitActivate(scannedCode, { arrivalNo });
}

export function submitTaskScan(printTaskId: string, scannedCode: string): Promise<ArrivalScanOk | ScanRejected> {
  return submitActivate(scannedCode, { printTaskId });
}

export async function listLockedArrivals(): Promise<ArrivalSummary[]> {
  const locked: ArrivalSummary[] = [];
  let page = 1;
  let total = 0;
  do {
    const result = await listArrivals(page, 50, true);
    total = result.total;
    for (const row of result.rows) {
      if (row.status === "LOCKED") locked.push(row);
    }
    page += 1;
  } while ((page - 1) * 50 < total && page <= 6);
  return locked;
}

export async function loadArrivalBoard(
  arrivalNo: string,
  humanCode?: string
): Promise<{ progress: ArrivalProgress; sku: SkuTally | null }> {
  let page = 1;
  let progress = await arrivalProgress(arrivalNo, page, 50);
  if (!humanCode) return { progress, sku: null };
  let line = progress.lines.find((item) => item.humanCode === humanCode) ?? null;
  while (!line && page * 50 < progress.lineTotal) {
    page += 1;
    progress = await arrivalProgress(arrivalNo, page, 50);
    line = progress.lines.find((item) => item.humanCode === humanCode) ?? null;
  }
  return { progress, sku: line ? skuTally(line) : null };
}

export async function submitResolvePutaway(
  scannedCode: string
): Promise<{ ok: true; shoe: PutawayTarget } | ScanRejected> {
  try {
    const found = await resolvePutaway(scannedCode);
    if (found.locationCode && found.pendingQty === 0) {
      return { ok: false, message: `这批已经在库位 ${found.locationCode}。不用再上架一次。` };
    }
    return { ok: true, shoe: found };
  } catch (error: unknown) {
    return { ok: false, message: errorText(error, "这不是鞋盒标或吊牌。请先扫鞋，再扫库位。") };
  }
}

export async function submitPutaway(locationCode: string, batchId: string): Promise<PutawaySubmitOk | ScanRejected> {
  try {
    const result = await putaway(locationCode, batchId);
    const message = result.left > 0
      ? `已上架 ${result.bound} 双到 ${result.locationCode}。这一批还剩 ${result.left} 双，再扫下一个库位。`
      : `已上架 ${result.bound} 双到 ${result.locationCode}。`;
    return {
      ok: true,
      locationCode: result.locationCode,
      bound: result.bound,
      left: result.left,
      message,
    };
  } catch (error: unknown) {
    return { ok: false, message: errorText(error, "库位没有更新。") };
  }
}

export async function listOpenOrders(): Promise<OutboundOrder[]> {
  const rows = await listOrders();
  return rows.filter((row) => row.status !== "SHIPPED");
}

export async function submitVerifyScan(orderNo: string, scannedCode: string): Promise<VerifySubmitOk | ScanRejected> {
  try {
    const result = await verifyScan(orderNo, scannedCode);
    try {
      const order = await getOrder(orderNo);
      return { ok: true, result, order };
    } catch (error: unknown) {
      if (passFinished(result)) return { ok: true, result, order: null };
      return { ok: false, message: errorText(error, "这一扫没有记上。") };
    }
  } catch (error: unknown) {
    return { ok: false, message: errorText(error, "这一扫没有记上。") };
  }
}

export function orderReadyToShip(order: { status: string; verifiedQty: number; totalQty: number }): boolean {
  return order.status !== "SHIPPED" && order.totalQty > 0 && order.verifiedQty >= order.totalQty;
}

export async function submitShipOrder(orderNo: string): Promise<{ ok: true } | ScanRejected> {
  try {
    await shipOrder(orderNo);
    return { ok: true };
  } catch (error: unknown) {
    return { ok: false, message: errorText(error, "这一步没有完成。") };
  }
}

export function playScanTone(kind: "pass" | "block"): void {
  try {
    const audio = new AudioContext();
    const osc = audio.createOscillator();
    const gain = audio.createGain();
    osc.frequency.value = kind === "pass" ? 880 : 180;
    gain.gain.value = 0.04;
    osc.connect(gain);
    gain.connect(audio.destination);
    osc.start();
    osc.stop(audio.currentTime + (kind === "pass" ? 0.12 : 0.45));
  } catch {
    return;
  }
}
